import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Plus, CheckSquare } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { WebsiteFilter } from '@/components/website/WebsiteFilter';
import { TaskCard } from '@/components/website/TaskCard';
import { SortableTaskCard } from '@/components/website/SortableTaskCard';
import { TaskModal } from '@/components/website/TaskModal';
import { WebsiteTaskBulkToolbar } from '@/components/website/WebsiteTaskBulkToolbar';
import { DroppableTaskColumn } from '@/components/website/DroppableTaskColumn';
import { useWebsiteTasks } from '@/hooks/useWebsiteTasks';
import { useWebsitePageTags } from '@/hooks/useWebsitePageTags';
import type { WebsiteName, WebsiteTask, TaskStatus } from '@/types/website';
import { TASK_STATUS_LABELS } from '@/types/website';

export default function CRMWebsiteTasks() {
  const navigate = useNavigate();
  const [websiteFilter, setWebsiteFilter] = useState<WebsiteName | 'all'>('all');
  const [pageTagFilter, setPageTagFilter] = useState<string>('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<WebsiteTask | null>(null);
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [activeId, setActiveId] = useState<string | null>(null);
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null);

  const filter = websiteFilter === 'all' ? undefined : websiteFilter;
  const pageFilter = pageTagFilter === 'all' ? undefined : pageTagFilter;
  const { tasks, createTask, updateTask, bulkUpdateTasks, bulkDeleteTasks, deleteTask } = useWebsiteTasks(filter, pageFilter);
  const { data: pageTags = [] } = useWebsitePageTags();

  const columns: TaskStatus[] = ['idee', 'in_uitvoering', 'voltooid'];

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const handleSave = (task: Partial<WebsiteTask>) => {
    if (task.id) {
      updateTask.mutate(task as WebsiteTask);
    } else {
      createTask.mutate(task as any);
    }
  };

  const toggleSelection = useCallback((taskId: string, event?: React.MouseEvent) => {
    const taskList = tasks;
    const taskIndex = taskList.findIndex(t => t.id === taskId);

    setSelectedIds(prev => {
      const next = new Set(prev);
      
      // Shift+Click for range selection
      if (event?.shiftKey && lastSelectedIndex !== null && taskIndex !== -1) {
        const start = Math.min(lastSelectedIndex, taskIndex);
        const end = Math.max(lastSelectedIndex, taskIndex);
        for (let i = start; i <= end; i++) {
          next.add(taskList[i].id);
        }
      } else {
        if (next.has(taskId)) {
          next.delete(taskId);
        } else {
          next.add(taskId);
        }
      }
      
      return next;
    });

    setLastSelectedIndex(taskIndex);
  }, [tasks, lastSelectedIndex]);

  const clearSelection = () => {
    setSelectedIds(new Set());
    setLastSelectedIndex(null);
  };

  const handleBulkUpdate = (updates: Partial<WebsiteTask>) => {
    bulkUpdateTasks.mutate({ ids: Array.from(selectedIds), updates });
    clearSelection();
  };

  const handleBulkDelete = () => {
    bulkDeleteTasks.mutate(Array.from(selectedIds));
    clearSelection();
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const activeTask = tasks.find(t => t.id === active.id);
    if (!activeTask) return;

    // Check if dropped on a column
    const targetStatus = columns.find(col => col === over.id);
    if (targetStatus && activeTask.status !== targetStatus) {
      updateTask.mutate({
        id: activeTask.id,
        status: targetStatus,
        completed_at: targetStatus === 'voltooid' ? new Date().toISOString() : null,
      } as WebsiteTask);
      return;
    }

    // Check if dropped on another task → use that task's status
    const overTask = tasks.find(t => t.id === over.id);
    if (overTask && activeTask.status !== overTask.status) {
      updateTask.mutate({
        id: activeTask.id,
        status: overTask.status,
        completed_at: overTask.status === 'voltooid' ? new Date().toISOString() : null,
      } as WebsiteTask);
    }
  };

  const activeTask = activeId ? tasks.find(t => t.id === activeId) : null;

  const handleTaskClick = (task: WebsiteTask) => {
    if (bulkMode) {
      toggleSelection(task.id);
    } else {
      navigate(`/crm/website/taken/${task.id}`);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Website Taken</h1>
        <div className="flex items-center gap-3">
          <Select value={pageTagFilter} onValueChange={setPageTagFilter}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Alle pagina's" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle pagina's</SelectItem>
              {pageTags.map((tag) => (
                <SelectItem key={tag} value={tag}>{tag}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <WebsiteFilter value={websiteFilter} onChange={setWebsiteFilter} />
          <Button
            variant={bulkMode ? 'secondary' : 'outline'}
            onClick={() => {
              setBulkMode(!bulkMode);
              if (bulkMode) clearSelection();
            }}
          >
            <CheckSquare className="h-4 w-4 mr-1" />
            Selecteren
          </Button>
          <Button onClick={() => { setSelectedTask(null); setModalOpen(true); }}>
            <Plus className="h-4 w-4 mr-1" />
            Nieuwe taak
          </Button>
        </div>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {columns.map(status => {
            const columnTasks = tasks.filter(t => t.status === status);
            return (
              <div key={status} className="space-y-3">
                <div className="flex items-center justify-between">
                  <h2 className="font-semibold text-sm">{TASK_STATUS_LABELS[status]}</h2>
                  <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">
                    {columnTasks.length}
                  </span>
                </div>
                <SortableContext
                  id={status}
                  items={columnTasks.map(t => t.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <DroppableTaskColumn id={status}>
                    {columnTasks.map(task => (
                      <SortableTaskCard
                        key={task.id}
                        task={task}
                        onClick={() => handleTaskClick(task)}
                        showCheckbox={bulkMode}
                        isSelected={selectedIds.has(task.id)}
                        onToggleSelect={(e) => toggleSelection(task.id, e)}
                        disabled={bulkMode}
                      />
                    ))}
                  </DroppableTaskColumn>
                </SortableContext>
              </div>
            );
          })}
        </div>

        <DragOverlay>
          {activeTask && (
            <TaskCard task={activeTask} isDragging />
          )}
        </DragOverlay>
      </DndContext>

      <WebsiteTaskBulkToolbar
        selectedCount={selectedIds.size}
        onClearSelection={clearSelection}
        onBulkUpdate={handleBulkUpdate}
        onBulkDelete={handleBulkDelete}
      />

      <TaskModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        task={selectedTask}
        onSave={handleSave}
        onDelete={(id) => deleteTask.mutate(id)}
        defaultWebsite={websiteFilter === 'all' ? undefined : websiteFilter}
      />
    </div>
  );
}
